import React, { forwardRef, useEffect, useRef } from 'react'

import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

interface ChatTextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  value: string
  onValueChange: (value: string) => void
  onEnterPress?: () => void
  autoResize?: boolean
  maxHeight?: number
}

const ChatTextarea = forwardRef<HTMLTextAreaElement, ChatTextareaProps>(({ value, onValueChange, onEnterPress, autoResize = true, maxHeight = 160, className, onKeyDown, ...rest }, ref) => {
  const innerRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (!autoResize) return

    const textarea = innerRef.current
    if (!textarea) return

    textarea.style.height = 'auto'
    const scrollHeight = textarea.scrollHeight
    const newHeight = Math.min(scrollHeight, maxHeight)
    textarea.style.height = `${newHeight}px`
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden'
  }, [value, autoResize, maxHeight])

  const handleRef = (node: HTMLTextAreaElement | null) => {
    innerRef.current = node

    if (typeof ref === 'function') {
      ref(node)
    } else if (ref) {
      ;(ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node
    }
  }

  return (
    <Textarea
      ref={handleRef}
      value={value}
      onChange={(event) => {
        onValueChange(event.target.value)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && !event.shiftKey && onEnterPress) {
          event.preventDefault()
          onEnterPress()
          return
        }

        onKeyDown?.(event)
      }}
      className={cn('flex-1 mb-0 border-primary focus:border-primary focus-visible:border-primary focus-visible:ring-primary focus-visible:ring-1', className)}
      {...rest}
    />
  )
})

ChatTextarea.displayName = 'ChatTextarea'

export default ChatTextarea
